import type { ICoord2D } from '@nova/types';

import { EPSILON_6 } from './math-utils';

export type { ILineSegment } from '@nova/types';

/**
 * True when the two finite segments share at least one point: they cross, meet at an endpoint, or
 * run along each other with overlapping extent.
 *
 * `tolerancePx` is the perpendicular distance at which a point counts as on a segment's line.
 * Exact grid geometry can pass `0`; float paths usually keep the default.
 */
export function lineSegmentsTouch(
  firstStart: ICoord2D,
  firstEnd: ICoord2D,
  secondStart: ICoord2D,
  secondEnd: ICoord2D,
  tolerancePx = EPSILON_6
): boolean {
  const secondStartSide = perpendicularDistance(firstStart, firstEnd, secondStart);
  const secondEndSide = perpendicularDistance(firstStart, firstEnd, secondEnd);
  const firstStartSide = perpendicularDistance(secondStart, secondEnd, firstStart);
  const firstEndSide = perpendicularDistance(secondStart, secondEnd, firstEnd);

  if (straddles(secondStartSide, secondEndSide, tolerancePx) && straddles(firstStartSide, firstEndSide, tolerancePx)) {
    return true;
  }

  return (isOnLine(secondStartSide, tolerancePx) && pointInSegmentBounds(firstStart, firstEnd, secondStart, tolerancePx))
    || (isOnLine(secondEndSide, tolerancePx) && pointInSegmentBounds(firstStart, firstEnd, secondEnd, tolerancePx))
    || (isOnLine(firstStartSide, tolerancePx) && pointInSegmentBounds(secondStart, secondEnd, firstStart, tolerancePx))
    || (isOnLine(firstEndSide, tolerancePx) && pointInSegmentBounds(secondStart, secondEnd, firstEnd, tolerancePx));
}

/** Signed perpendicular distance from `point` to the infinite line through `start` and `end`. */
export function perpendicularDistanceToLine(start: ICoord2D, end: ICoord2D, point: ICoord2D): number {
  return perpendicularDistance(start, end, point);
}

/** Closest point on the finite segment `a`→`b` to `p`, plus clamped parameter `t` in [0, 1]. */
export interface IClosestPointOnSegmentResult extends ICoord2D {
  t: number;
}

/**
 * Closest point on the open polyline `points[i]→points[i+1]` to `p`.
 * `startSeg` / `endSegExclusive` limit which segments are considered.
 */
export interface IClosestPointOnPolylineResult extends ICoord2D {
  segmentIndex: number;
  t: number;
  distSq: number;
}

/**
 * Closest point on the finite segment `a`→`b` to `p`, written into `out` to avoid an allocation.
 * @param a Segment start.
 * @param b Segment end.
 * @param p Point to measure from.
 * @param out Receives the closest point and `t`, its clamped fraction along `a`→`b` in [0, 1].
 * @returns Squared distance from `p` to the closest point.
 */
export function writeClosestPointOnSegment(a: ICoord2D, b: ICoord2D, p: ICoord2D, out: IClosestPointOnSegmentResult): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  let t = 0;
  if (abLenSq > 0) {
    t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / abLenSq;
    if (t < 0) { t = 0; }
    else if (t > 1) { t = 1; }
  }

  out.t = t;
  out.x = a.x + abx * t;
  out.y = a.y + aby * t;
  const dx = out.x - p.x;
  const dy = out.y - p.y;
  return dx * dx + dy * dy;
}

/** Closest point on the finite segment `a`→`b` to `p`, including the clamped parameter `t`. */
export function closestPointOnSegmentDetails(a: ICoord2D, b: ICoord2D, p: ICoord2D): IClosestPointOnSegmentResult {
  const out: IClosestPointOnSegmentResult = { x: 0, y: 0, t: 0 };
  writeClosestPointOnSegment(a, b, p, out);
  return out;
}

/** Closest point on the finite segment `a`→`b` to `p`. */
export function closestPointOnSegment(a: ICoord2D, b: ICoord2D, p: ICoord2D): ICoord2D {
  const { x, y } = closestPointOnSegmentDetails(a, b, p);
  return { x, y };
}

/**
 * Closest point on the open polyline `points[i]→points[i+1]` to `p`.
 * Returns null when there are fewer than two points.
 */
export function closestPointOnPolyline(
  points: ICoord2D[],
  p: ICoord2D,
  startSeg = 0,
  endSegExclusive?: number
): IClosestPointOnPolylineResult | null {
  if (points.length < 2) {
    return null;
  }

  const lastSeg = points.length - 1;
  const from = Math.max(0, Math.min(startSeg, lastSeg - 1));
  const toExclusive = endSegExclusive === undefined
    ? lastSeg
    : Math.max(from + 1, Math.min(endSegExclusive, lastSeg));

  let best: IClosestPointOnPolylineResult | null = null;

  for (let i = from; i < toExclusive; i++) {
    const projected = closestPointOnSegmentDetails(points[i], points[i + 1], p);
    const dx = p.x - projected.x;
    const dy = p.y - projected.y;
    const distSq = dx * dx + dy * dy;
    if (!best || distSq < best.distSq) {
      best = {
        x: projected.x,
        y: projected.y,
        t: projected.t,
        segmentIndex: i,
        distSq
      };
    }
  }

  return best;
}

/** Project `p` onto the polyline starting near `waypointIndex` (previous segment onward). */
export function projectPointOntoCurrentPath(
  points: ICoord2D[],
  waypointIndex: number,
  x: number,
  y: number
): ICoord2D | null {
  if (points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    return { x: points[0].x, y: points[0].y };
  }

  const startSeg = Math.max(0, Math.min(waypointIndex, points.length - 1) - 1);
  const closest = closestPointOnPolyline(points, { x, y }, startSeg);
  if (!closest) {
    return null;
  }

  return { x: closest.x, y: closest.y };
}

/** Distance from a point to the polyline, or +Infinity if projection fails. */
export function distanceToPolyline(points: ICoord2D[], point: ICoord2D): number {
  if (points.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  if (points.length === 1) {
    return Math.hypot(point.x - points[0].x, point.y - points[0].y);
  }

  const closest = closestPointOnPolyline(points, point);
  if (!closest) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.hypot(point.x - closest.x, point.y - closest.y);
}

/** Cumulative arc length to each vertex: lengths[i] = distance along path from points[0] to points[i]. */
export function polylineCumulativeLengths(points: ICoord2D[]): number[] {
  const lengths = new Array<number>(points.length);
  if (points.length === 0) {
    return lengths;
  }

  lengths[0] = 0;
  for (let i = 1; i < points.length; i++) {
    lengths[i] = lengths[i - 1] +
      Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return lengths;
}

export type PathProjection = {
  x: number;
  y: number;
  arcLengthPx: number;
  distToPathPx: number;
  /** Next waypoint index to seek after this projection */
  waypointIndex: number;
};

const DEFAULT_SEGMENT_THRESHOLD = 0.9;
const DEFAULT_ARRIVAL_EPSILON_PX = 6;

function nextWaypointIndex(
  pointsLength: number,
  segmentIndex: number,
  t: number,
  point: ICoord2D,
  segmentEnd: ICoord2D,
  segmentThreshold: number,
  arrivalEpsilonPx: number
): number {
  const arrivalEpsSq = arrivalEpsilonPx * arrivalEpsilonPx;
  const dx = point.x - segmentEnd.x;
  const dy = point.y - segmentEnd.y;
  const endDistSq = dx * dx + dy * dy;
  const endOfSeg = segmentIndex + 1;
  const pastSegEnd = t >= segmentThreshold || endDistSq <= arrivalEpsSq;
  return pastSegEnd
    ? Math.min(endOfSeg + 1, pointsLength - 1)
    : Math.min(endOfSeg, pointsLength - 1);
}

/** Project a world point onto the polyline and return arc-length progress. */
export function projectOntoPath(
  points: ICoord2D[],
  point: ICoord2D,
  segmentThreshold = DEFAULT_SEGMENT_THRESHOLD,
  arrivalEpsilonPx = DEFAULT_ARRIVAL_EPSILON_PX
): PathProjection | null {
  if (points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    const dist = Math.hypot(point.x - points[0].x, point.y - points[0].y);
    return {
      x: points[0].x,
      y: points[0].y,
      arcLengthPx: 0,
      distToPathPx: dist,
      waypointIndex: 0
    };
  }

  const closest = closestPointOnPolyline(points, point);
  if (!closest) {
    return null;
  }

  const lengths = polylineCumulativeLengths(points);
  const segStartArc = lengths[closest.segmentIndex];
  const segLen = lengths[closest.segmentIndex + 1] - segStartArc;
  const arcLengthPx = segStartArc + closest.t * segLen;
  const end = points[closest.segmentIndex + 1];

  return {
    x: closest.x,
    y: closest.y,
    arcLengthPx,
    distToPathPx: Math.sqrt(closest.distSq),
    waypointIndex: nextWaypointIndex(
      points.length,
      closest.segmentIndex,
      closest.t,
      point,
      end,
      segmentThreshold,
      arrivalEpsilonPx
    )
  };
}

/** Sample a world position on the polyline at the given arc length. */
export function pointAtArcLength(
  points: ICoord2D[],
  arcLengthPx: number,
  segmentThreshold = DEFAULT_SEGMENT_THRESHOLD
): PathProjection | null {
  if (points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    return {
      x: points[0].x,
      y: points[0].y,
      arcLengthPx: 0,
      distToPathPx: 0,
      waypointIndex: 0
    };
  }

  const lengths = polylineCumulativeLengths(points);
  const total = lengths[lengths.length - 1];
  const clamped = Math.max(0, Math.min(arcLengthPx, total));

  if (clamped <= 0) {
    return {
      x: points[0].x,
      y: points[0].y,
      arcLengthPx: 0,
      distToPathPx: 0,
      waypointIndex: Math.min(1, points.length - 1)
    };
  }

  if (clamped >= total) {
    const last = points.length - 1;
    return {
      x: points[last].x,
      y: points[last].y,
      arcLengthPx: total,
      distToPathPx: 0,
      waypointIndex: last
    };
  }

  let seg = 0;
  while (seg < lengths.length - 2 && lengths[seg + 1] < clamped) {
    seg++;
  }

  const segStart = lengths[seg];
  const segEnd = lengths[seg + 1];
  const segLen = segEnd - segStart;
  const t = segLen > 0 ? (clamped - segStart) / segLen : 0;
  const a = points[seg];
  const b = points[seg + 1];

  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    arcLengthPx: clamped,
    distToPathPx: 0,
    waypointIndex: t >= segmentThreshold
      ? Math.min(seg + 2, points.length - 1)
      : Math.min(seg + 1, points.length - 1)
  };
}

/**
 * Intersection of the ray `rayStart`→`rayEnd` (extends beyond `rayEnd`) with the finite segment
 * `segStart`→`segEnd`, or null when they miss.
 */
export function getRaySegmentIntersection(rayStart: ICoord2D, rayEnd: ICoord2D, segStart: ICoord2D, segEnd: ICoord2D): ICoord2D | null {
  const rpx = rayEnd.x - rayStart.x;
  const rpy = rayEnd.y - rayStart.y;
  const spx = segEnd.x - segStart.x;
  const spy = segEnd.y - segStart.y;

  const denominator = rpx * spy - rpy * spx;
  if (denominator === 0) {
    return null;
  }

  const t = ((segStart.x - rayStart.x) * spy - (segStart.y - rayStart.y) * spx) / denominator;
  const u = ((segStart.x - rayStart.x) * rpy - (segStart.y - rayStart.y) * rpx) / denominator;

  if (t >= 0 && u >= 0 && u <= 1) {
    return {
      x: rayStart.x + t * rpx,
      y: rayStart.y + t * rpy
    };
  }

  return null;
}

/** Where two finite segments meet. */
export interface ISegmentIntersection {
  /** Shared point of the two segments. */
  point: ICoord2D;
  /** Fraction along `from`→`to` where `point` sits, in [0, 1]. */
  t: number;
}

/**
 * Intersection of two finite segments, or null when they miss, are parallel, or only meet past an endpoint.
 * @param from Start of the segment that `t` is measured along.
 * @param to End of the segment that `t` is measured along.
 * @param segStart Start of the segment being tested against.
 * @param segEnd End of the segment being tested against.
 */
export function getSegmentSegmentIntersection(from: ICoord2D, to: ICoord2D, segStart: ICoord2D, segEnd: ICoord2D): ISegmentIntersection | null {
  const rpx = to.x - from.x;
  const rpy = to.y - from.y;
  const spx = segEnd.x - segStart.x;
  const spy = segEnd.y - segStart.y;

  const denominator = rpx * spy - rpy * spx;
  if (denominator === 0) { return null; }

  const t = ((segStart.x - from.x) * spy - (segStart.y - from.y) * spx) / denominator;
  const u = ((segStart.x - from.x) * rpy - (segStart.y - from.y) * rpx) / denominator;
  const tolerance = EPSILON_6;
  if (t < -tolerance || t > 1 + tolerance || u < -tolerance || u > 1 + tolerance) { return null; }

  const clampedT = Math.min(1, Math.max(0, t));
  return {
    point: { x: from.x + clampedT * rpx, y: from.y + clampedT * rpy },
    t: clampedT
  };
}

/** Where a ray first meets a polygon edge, including that edge. */
export interface IRayPolygonEdgeHit {
  point: ICoord2D;
  edgeStart: ICoord2D;
  edgeEnd: ICoord2D;
  distance: number;
}

/** Closest point on a polygon's boundary to `from` along the ray toward `to`, or null when no edge is hit. */
export function getClosestRayEdgeHitOnPolygon(from: ICoord2D, to: ICoord2D, polygon: ICoord2D[]): IRayPolygonEdgeHit | null {
  let closestHit: IRayPolygonEdgeHit | null = null;

  for (let i = 0; i < polygon.length; i++) {
    const edgeStart = polygon[i];
    const edgeEnd = polygon[(i + 1) % polygon.length];
    const intersection = getRaySegmentIntersection(from, to, edgeStart, edgeEnd);
    if (!intersection) {
      continue;
    }

    const distance = Math.hypot(intersection.x - from.x, intersection.y - from.y);
    if (!closestHit || distance < closestHit.distance) {
      closestHit = { point: { x: intersection.x, y: intersection.y }, edgeStart, edgeEnd, distance };
    }
  }

  return closestHit;
}

function perpendicularDistance(start: ICoord2D, end: ICoord2D, point: ICoord2D): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  return (((dx * (point.y - start.y)) - (dy * (point.x - start.x))) / length);
}

function isOnLine(distance: number, tolerancePx: number): boolean {
  return Math.abs(distance) <= tolerancePx;
}

function straddles(firstDistance: number, secondDistance: number, tolerancePx: number): boolean {
  return (firstDistance > tolerancePx && secondDistance < -tolerancePx)
    || (firstDistance < -tolerancePx && secondDistance > tolerancePx);
}

/** True when `point`, already known to be on the segment's line, falls between its endpoints. */
function pointInSegmentBounds(start: ICoord2D, end: ICoord2D, point: ICoord2D, tolerancePx: number): boolean {
  return point.x >= Math.min(start.x, end.x) - tolerancePx
    && point.x <= Math.max(start.x, end.x) + tolerancePx
    && point.y >= Math.min(start.y, end.y) - tolerancePx
    && point.y <= Math.max(start.y, end.y) + tolerancePx;
}
